import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { BehaviorSubject, catchError, finalize, of, switchMap, tap } from 'rxjs';
import { $themeConfig } from '../../theme.config';
import { translateFileLang } from '../../shared/utils/translate-file-lang';
import { ResourcesService } from './api/resources.service';

@Injectable()
export class AppService {
  storeData!: StoreData;

  /**
   * True until JSON + Resources API have settled for the active locale.
   * Same boot gate as IMS — avoid flashing raw keys before the API merge completes.
   */
  readonly translationsLoading$ = new BehaviorSubject<boolean>(true);

  private languageLoadSeq = 0;

  constructor(
    public translate: TranslateService,
    public store: Store<{ index: StoreData }>,
    private readonly resourcesService: ResourcesService,
  ) {
    this.initStoreData();
  }

  initStoreData() {
    this.store.select((d) => d.index).subscribe((d) => {
      this.storeData = d;
    });

    // set default styles
    let val: string | null = localStorage.getItem('theme'); // light, dark, system
    val = val || $themeConfig.theme;
    this.store.dispatch({ type: 'toggleTheme', payload: val });

    val = localStorage.getItem('menu'); // vertical, collapsible-vertical, horizontal
    val = val || $themeConfig.menu;
    this.store.dispatch({ type: 'toggleMenu', payload: val });

    val = localStorage.getItem('layout'); // full, boxed-layout
    val = val || $themeConfig.layout;
    this.store.dispatch({ type: 'toggleLayout', payload: val });

    val = localStorage.getItem('i18n_locale'); // en, da, de, el, es, fr, hu, it, ja, pl, pt, ru, sv, tr, zh
    val = val || $themeConfig.locale;
    if (val === 'ar') {
      localStorage.setItem('i18n_locale', 'sa');
      val = 'sa';
    }

    const list = this.storeData.languageList;
    const item = list.find((entry) => entry.code === val) ?? null;
    this.toggleLanguage(item);

    val = localStorage.getItem('rtlClass'); // rtl, ltr
    val = val || $themeConfig.rtlClass;
    this.store.dispatch({ type: 'toggleRTL', payload: val });

    val = localStorage.getItem('animation'); // animate__fadeIn, animate__fadeInDown, animate__fadeInUp, animate__fadeInLeft, animate__fadeInRight, animate__slideInDown, animate__slideInLeft, animate__slideInRight, animate__zoomIn
    val = val || $themeConfig.animation;
    this.store.dispatch({ type: 'toggleAnimation', payload: val });

    val = localStorage.getItem('navbar'); // navbar-sticky, navbar-floating, navbar-static
    val = val || $themeConfig.navbar;
    this.store.dispatch({ type: 'toggleNavbar', payload: val });

    const semidarkValue = localStorage.getItem('semidark');
    const semidark = semidarkValue === 'true' ? true : $themeConfig.semidark;
    this.store.dispatch({ type: 'toggleSemidark', payload: semidark });
  }

  toggleLanguage(item: LanguageItem | null) {
    let lang: LanguageItem | null = item;
    if (!lang) {
      let code: string | null = this.translate.getCurrentLang() || null;
      if (!code) {
        code = localStorage.getItem('i18n_locale');
      }
      const listCode = translateFileLang(code);

      lang = this.storeData.languageList.find((d) => d.code === listCode) || null;
    }

    if (!lang) {
      lang = this.storeData.languageList.find((d) => d.code === 'en') || null;
    }

    if (!lang) {
      this.translationsLoading$.next(false);
      return null;
    }

    const locale = lang.code;
    const fileLanguage = translateFileLang(locale);
    const isArabic = locale === 'sa' || locale === 'ar';

    // Persist locale/RTL synchronously so a page reload (header language switch) keeps Arabic.
    document.documentElement.lang = isArabic ? 'ar' : locale;
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
    this.store.dispatch({ type: 'toggleRTL', payload: isArabic ? 'rtl' : 'ltr' });
    this.store.dispatch({ type: 'toggleLocale', payload: locale });

    const loadSeq = ++this.languageLoadSeq;
    this.translationsLoading$.next(true);

    // Same as IMS: empty static JSON stub via HttpLoader, then merge API (API wins).
    this.translate
      .use(fileLanguage)
      .pipe(
        switchMap(() => this.loadApiTranslations(locale, fileLanguage)),
        finalize(() => {
          if (loadSeq === this.languageLoadSeq) {
            this.translationsLoading$.next(false);
          }
        }),
      )
      .subscribe({
        error: () => {
          // API/use failures are non-fatal; locale already persisted above.
        },
      });

    return lang;
  }

  changeAnimation(type: 'add' | 'remove' = 'add') {
    if (this.storeData.animation) {
      const ele: HTMLElement | null = document.querySelector('.animation');
      if (type === 'add') {
        ele?.classList.add('animate__animated');
        ele?.classList.add(this.storeData.animation);
      } else {
        ele?.classList.remove('animate__animated');
        ele?.classList.remove(this.storeData.animation);
      }
    }
  }

  /**
   * Reloads static JSON then re-merges Resources API for the active locale.
   * Call after Resources admin create/update/delete so new keys appear without a page reload.
   */
  refreshApiTranslations(): void {
    const locale =
      this.translate.getCurrentLang() || localStorage.getItem('i18n_locale') || 'en';
    const fileLanguage = translateFileLang(locale);
    const loadSeq = ++this.languageLoadSeq;
    this.translationsLoading$.next(true);

    this.translate
      .reloadLang(fileLanguage)
      .pipe(
        switchMap(() => this.loadApiTranslations(locale, fileLanguage)),
        finalize(() => {
          if (loadSeq === this.languageLoadSeq) {
            this.translationsLoading$.next(false);
          }
        }),
        catchError(() => of(null)),
      )
      .subscribe({
        error: () => {
          // Non-fatal: existing dictionary remains.
        },
      });
  }

  /**
   * Loads LMS Resources for the selected locale and merges into the active dictionary.
   * Failure is non-fatal: static JSON stubs remain available.
   */
  private loadApiTranslations(locale: string, fileLanguage: string) {
    return this.resourcesService.getTranslationDictionary(locale).pipe(
      tap((apiTranslations) => {
        if (this.isUsableDictionary(apiTranslations)) {
          // shouldMerge = true → keep JSON keys; API values override matching keys.
          this.translate.setTranslation(fileLanguage, apiTranslations, true);
        }
      }),
      catchError(() => of(null)),
    );
  }

  private isUsableDictionary(value: TranslationObject | null | undefined): value is TranslationObject {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}

interface LanguageItem {
  code: string;
  [key: string]: unknown;
}

interface StoreData {
  languageList: LanguageItem[];
  animation?: string;
  [key: string]: unknown;
}
